import { useState } from "react";
import { useTranslation } from "react-i18next";
import MuiDrawer from "@mui/material/Drawer";
import Grid from "@/config/Grid/Grid";
import Button from "@/components/Button/Button";
import IconButton from "@/components/IconButton/IconButton";
import OtpInput from "@/components/OtpInput/OtpInput";
import TextField from "@/components/TextField/TextField";
import { drawerStyles } from "@/components/Drawer/Drawer.styles";
import drawerCss from "@/components/Drawer/Drawer.module.scss";
import { isValidPhone } from "@/helpers/contactValue";
import { useLoyaltyActions } from "@/store/useLoyaltyStore";
import styles from "./SignInDrawer.module.scss";

interface SignInDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Signing in, in two steps: the contact, then the code that proves it is
 * theirs.
 *
 * Nothing is sent and nothing is checked: there is no endpoint to ask for a
 * code and none to verify it against. Any six digits pass, and what the step
 * records is the flag on the guest, so the wiring has one place to become
 * real. See identityVerified in useLoyaltyStore.
 */
function SignInDrawer(props: SignInDrawerProps) {
  const { open, onClose } = props;
  const { t } = useTranslation();
  const { verifyIdentity } = useLoyaltyActions();

  const [step, setStep] = useState<"contact" | "code">("contact");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  /** The verdict from the last blur, as on the contact form. */
  const [phoneError, setPhoneError] = useState(false);

  const phoneValid = phone.trim() === "" || isValidPhone(phone);
  const contact = phone.trim();
  const canSendCode = Boolean(contact) && phoneValid;

  const close = () => {
    onClose();
    setStep("contact");
    setCode("");
  };

  const handleVerify = () => {
    if (code.length !== 6) return;
    verifyIdentity({ phone });
    close();
  };

  return (
    <MuiDrawer sx={drawerStyles} anchor="bottom" open={open} onClose={close}>
      <Grid>
        <div
          className={`start-1 col-4 align-start ${drawerCss.drawerLayoutWrapper}`}
        >
          <div className={drawerCss.drawerLayoutHeader}>
            <IconButton
              iconName="las la-angle-left icon-big"
              onClick={step === "code" ? () => setStep("contact") : close}
              color="primary"
            />
            <p className={`body-bold-large ${drawerCss.drawerLayoutTitle}`}>
              {step === "code"
                ? t("loyalty.signIn.otpTitle")
                : t("loyalty.signIn.title")}
            </p>
            <IconButton
              iconName=""
              onClick={() => {}}
              color="primary"
              variant="text"
            />
          </div>

          <div className={styles.signInContent}>
            {step === "contact" ? (
              <>
                <p className={`body-medium ${styles.signInHint}`}>
                  {t("loyalty.signIn.intro")}
                </p>
                <TextField
                  id="signin-phone"
                  label={t("loyalty.contactPhoneLabel")}
                  placeholder={t("loyalty.contactPhonePlaceholder")}
                  focused
                  hasError={phoneError}
                  helperText={
                    phoneError ? t("loyalty.contactPhoneInvalid") : undefined
                  }
                  field={{
                    name: "signin-phone",
                    value: phone,
                    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                      setPhone(event.target.value);
                      setPhoneError(false);
                    },
                    onBlur: () => setPhoneError(!phoneValid),
                  }}
                />
                <Button
                  onClick={() => setStep("code")}
                  label={t("loyalty.button.sendCode")}
                  variant="contained"
                  color="primary"
                  maxWidth
                  mobile={true}
                  disabled={!canSendCode}
                />
              </>
            ) : (
              <>
                <p className={`body-medium ${styles.signInHint}`}>
                  {t("loyalty.signIn.otpIntro", { contact })}
                </p>
                <OtpInput otp={code} setOtp={setCode} />
                <Button
                  onClick={handleVerify}
                  label={t("loyalty.button.verify")}
                  variant="contained"
                  color="primary"
                  maxWidth
                  mobile={true}
                  disabled={code.length !== 6}
                />
              </>
            )}
          </div>
        </div>
      </Grid>
    </MuiDrawer>
  );
}

export default SignInDrawer;
